import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { compact, debounce, extend, uniqBy } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { DateComponent } from 'app/components/DateComponent'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { ContactCell } from 'app/components/shared/ContactCell'
import useFakeProgress from 'app/hooks/fakeProgress'
import type { ContactJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { graphql } from 'app/generated/gql'
import { gqlMutation } from 'app/lib/graphqlClient'

const REMATCH_ACTIVITY_MUTATION = graphql(`
  mutation RematchActivity($id: ID!, $contactId: String) {
    rematchActivity(id: $id, contactId: $contactId) {
      id
    }
  }
`)
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'

interface UseMatchPatientModalArgs {
  contact: ContactJson
  patient: PatientJson
  done: () => void
}

export type UseMatchPatientModalReturn = {
  patient: PatientJson
  contact: ContactJson
  fetcher: Record<string, never>
  data: (ContactJson & { matchTypes?: string[] })[]
  columns: ColumnDef<ContactJson & { matchTypes?: string[] }, any>[]
  matchedContact: ContactJson | null
  selectedContact: ContactJson | null
  showConfirmationStep: boolean
  showNewContactStep: boolean
  inProgress: boolean
  progress: number
  isLoading: boolean
  searchWorkspace: boolean
  setPage: React.Dispatch<React.SetStateAction<number>>
  setShowConfirmationStep: React.Dispatch<React.SetStateAction<boolean>>
  setShowNewContactStep: React.Dispatch<React.SetStateAction<boolean>>
  onSearchValueChange: (val: string) => void
  onSearchWorkspaceToggle: (toggle: boolean) => void
  matchPatient: () => Promise<void>
  createNewContact: () => Promise<void>
  goToContact: () => void
  done: () => void
}

export function useMatchPatientModal({ contact, patient, done }: UseMatchPatientModalArgs): UseMatchPatientModalReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const [contacts, setContacts] = useState<ContactJson[]>()
  const [topMatches, setTopMatches] = useState<ContactJson[]>([])
  const workspace = useWorkspace()
  const contactStore = useContactStore()
  const [page, setPage] = useState<number>(0)
  const [matchedContact, setMatchedContact] = useState<ContactJson | null>(null)
  const [selectedContact, setSelectedContact] = useState<ContactJson | null>(null)
  const [showConfirmationStep, setShowConfirmationStep] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [progress, progressStart, progressEnd] = useFakeProgress()
  const [searchWorkspace, setSearchWorkspace] = useState(false)
  const [searchValue, setSearchValue] = useState(``)
  const [isLoading, setIsLoading] = useState(false)
  const [showNewContactStep, setShowNewContactStep] = useState(false)
  const [newContactId, setNewContactId] = useState<string>(contact.id!)

  const user = useUser()

  useEffect(() => {
    if (patient) {
      setMatchedContact(contact)
    }
  }, [patient])

  useEffect(() => {
    if (inProgress) {
      progressStart()
    }
  }, [inProgress])

  const defaultColumns = (
    selectedContact: ContactJson | null,
    matchedContact: ContactJson | null,
    onContactHandler: (contact: ContactJson) => void,
    onSelectContact: (contact: ContactJson) => void,
  ): ColumnDef<ContactJson & { matchTypes?: string[] }, any>[] =>
    compact([
      {
        accessorFn: (row) => row.id,
        id: `actions`,
        header: ``,
        maxSize: 50,
        enableResizing: false,
        cell: function Actions({ row }) {
          useEffect(() => {
            if (matchedContact?.id === row.original.id || contact?.id === row.original.id) {
              row.pin(`top`)
            }
          }, [])
          return (
            <div className="flex items-center justify-center">
              <input
                type="radio"
                checked={
                  selectedContact?.id === row.original.id || (!selectedContact && contact.id === row.original.id)
                }
                onChange={() => onSelectContact(row.original)}
                className="h-4 w-4 border-strong text-blue-600 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )
        },
        meta: {
          defaultVisible: true,
          pinned: false,
          tooltipClassName: `w-full`,
        },
        enableSorting: false,
        enableHiding: false,
        size: 50,
        minSize: 50,
      },
      {
        id: `name`,
        accessorFn: (row) => row.name,
        header: `Name`,
        enableResizing: true,
        meta: {
          group: `General`,
          defaultVisible: true,
          pinned: false,
          sortKey: `name.plain`,
          onClick: (row) => onContactHandler(row),
          tooltipText: (row) => row.name ?? ``,
        },
        enableHiding: false,
        minSize: 100,
        cell: (props) => <ContactCell contact={props.row.original} matchedContact={matchedContact} />,
      },
      {
        id: `Patient Linked`,
        accessorKey: `patientIds`,
        header: `Patient Linked`,
        meta: {
          defaultVisible: true,
          sortKey: `patientIds`,
        },
        cell: (props) => (
          <span
            className={classNames(
              `flex h-6 items-center rounded border px-1.5 text-xs font-medium not-italic leading-4`,
              props.getValue() ? `bg-green-100 text-green-700` : `bg-red-100 text-red-700`,
            )}
          >
            {props.getValue() ? `Linked` : `Not Linked`}
          </span>
        ),
      },
      {
        id: `locationId`,
        accessorKey: `locationId`,
        header: `Location`,
        cell: ({ row }) => <LazyLocationCell locationId={row.original.locationId!} />,
        meta: {
          defaultVisible: true,
        },
        enableSorting: false,
      },
      user?.type === `super` && {
        id: `matchTypes`,
        accessorKey: `matchTypes`,
        header: `Match Types`,
        meta: { group: `General`, defaultVisible: true, pinned: false, sortKey: `matchTypes.keyword` },
      },
      {
        id: `homePhone`,
        accessorKey: `homePhone`,
        header: `Home Phone`,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `homePhone.keyword`,
        },
        cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
      },
      {
        id: `cellPhone`,
        accessorKey: `cellPhone`,
        header: `Cell Phone`,
        cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `cellPhone.keyword`,
        },
      },
      {
        id: `firstAppointmentDate`,
        accessorKey: `firstAppointmentDate`,
        header: `First Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `firstAppointmentDate`,
        },
      },
      {
        id: `lastAppointmentDate`,
        accessorKey: `lastAppointmentDate`,
        header: `Last Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `lastAppointmentDate`,
        },
      },
    ])

  const data = useMemo(
    () =>
      searchValue !== ``
        ? uniqBy(compact([matchedContact, ...(contacts || [])]), `id`)
        : uniqBy(compact([...topMatches]), `id`),
    [searchValue, contacts, topMatches, matchedContact],
  )

  useEffect(() => {
    if (searchValue !== ``) {
      bg(async () => {
        const specificityFilter = searchWorkspace
          ? `workspaceId=${contact?.workspaceId}`
          : `locationIds=${contact?.locationId}`
        const result = await contactStore.fetchList({
          search: `${specificityFilter} AND query:${searchValue} AND page:${page}`,
        })
        const resp = result.data as any
        if (resp?.contacts?.hits) {
          setContacts((data) =>
            uniqBy([...(data || []), ...resp.contacts.hits], `id`).filter((c) => c.id !== matchedContact?.id),
          )
        }
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [searchValue, searchWorkspace, page])

  useEffect(() => {
    if (contact) {
      setIsLoading(true)
      let additionalSearchesString = JSON.stringify({
        firstAppointmentMadeDate:
          contact.firstAppointmentMadeDate && moment(contact.firstAppointmentMadeDate).format(`YYYY-MM-DD`),
        patientCreatedDate: contact?.patientCreatedDate && moment(contact?.patientCreatedDate).format(`YYYY-MM-DD`),
        firstAppointmentDate: contact.firstAppointmentDate && moment(contact.firstAppointmentDate).format(`YYYY-MM-DD`),
        birthDate: contact.birthDate && moment(contact.birthDate).format(`YYYY-MM-DD`),
      })
      bg(async () => {
        let data = await window
          .fetch(
            `/api/contacts/matches?name=${contact.name}&locationId=${contact?.locationId}&phone=${
              contact.cellPhone || contact?.homePhone
            }&additionalSearches=${additionalSearchesString}&limit=5`,
            { credentials: `include` },
          )
          .then((r) => r.json())
        let _topMatches = data?.map((m) => extend(m.contact, { matchTypes: m.matchTypes }))
        debug(`rematch`, `top matches`, data, _topMatches)

        setTopMatches(_topMatches)
        setIsLoading(false)
      })
    }
  }, [contact])

  const handleSelectContact = (selectedContact: ContactJson) => {
    if (selectedContact.id === contact?.id) {
      setSelectedContact(null)
    } else {
      setSelectedContact(selectedContact)
    }
  }

  const columns = useMemo(
    () =>
      defaultColumns(
        selectedContact,
        matchedContact,
        (contact) => {
          const url = `/contacts?tab=all&drawer=${contact.id}&query=${contact.name}&wId=${workspace?.id ?? null}`
          window.open(url, `_blank`)
        },
        handleSelectContact,
      ),
    [selectedContact, matchedContact],
  )

  const matchPatient = async () => {
    setInProgress(true)
    const result = await gqlMutation(REMATCH_ACTIVITY_MUTATION, {
      id: `Patient-${patient.id}`,
      contactId: selectedContact?.id,
    })
    progressEnd()
    if (result.error) {
      notifyError(`Problem with linking patient to selected contact: ${result.error.message}`)
    } else {
      notifySuccess(`Patient linked successfully`)
      const newId = (result.data as any)?.rematchActivity?.id
      if (newId) setNewContactId(newId)
    }
  }

  const goToContact = () => {
    done()
    if (searchParams.get(`drawer`) === newContactId) {
      debug(`rematch`, `reloading`)
      window.location.reload()
      return
    } else {
      debug(`rematch`, `setting drawer`, newContactId)
      setSearchParams((prev) => {
        prev.set(`drawer`, contact?.id ?? ``)
        return prev
      })
    }
  }

  const onSearchValueChange = debounce((val) => {
    setSearchValue(val)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }, 250)

  const onSearchWorkspaceToggle = (toggle: boolean) => {
    setSearchWorkspace(toggle)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }

  const createNewContact = async () => {
    setInProgress(true)
    const result = await gqlMutation(REMATCH_ACTIVITY_MUTATION, { id: `Patient-${patient.id}`, contactId: `new` })
    progressEnd()
    if (result.error) {
      notifyError(`Problem with creating a new contact: ${result.error.message}`)
    } else {
      notifySuccess(`New contact created`)
      const newId = (result.data as any)?.rematchActivity?.id
      if (newId) setNewContactId(newId)
    }
  }

  return {
    patient,
    contact,
    fetcher: {} as any,
    data,
    columns,
    matchedContact,
    selectedContact,
    showConfirmationStep,
    showNewContactStep,
    inProgress,
    progress,
    isLoading,
    searchWorkspace,
    setPage,
    setShowConfirmationStep,
    setShowNewContactStep,
    onSearchValueChange,
    onSearchWorkspaceToggle,
    matchPatient,
    createNewContact,
    goToContact,
    done,
  }
}
